import useExcelDownload from '@/hooks/useDownloadExcel'
import { keepPreviousData, useMutation, useQuery, UseQueryResult } from '@tanstack/react-query'
import {
  postBrandList,
  postMFSPartnerList,
  postPromotionSettlementDetails,
  postPromotionSettlementSearch,
  getPromotionChargeBases,
  getPromotionStatusList,
  getServiceTypeList,
  patchStopPromotion,
  postPromotion,
  postPromotionList,
  putPromotion,
} from '@/services/settlement/settlementPromotion/api'
import {
  GetPromotionChargeBaseRes,
  PostPromotionListReq,
  PostPromotionListRes,
  PostPromotionReq,
  PromotionSettlementLineReq,
  PromotionSettlementLineRes,
  PromotionSettlementSummaryReq,
  PromotionSettlementSummaryRow,
  PutPromotionReq,
} from '@/services/settlement/settlementPromotion/types'
import { buildPromotionListSubmitParams } from '@/services/settlement/settlementPromotion/serviceChargeItem'
import { normalizeDiscountScale, stripTrailingZeros } from '@/services/settlement/settlementPromotion/discountValue'
import { sortBy } from 'lodash-es'

// 할인값이 응답부터 문자열이라 표시용 변환이 없다 — 이름만 남긴다 (LOGISPRD-11361)
export type PostPromotionListResDisplayType = PostPromotionListRes

// 프로모션 목록 조회 쿼리
export const usePostPromotionList = (
  params: ApiPaginationReq<PostPromotionListReq>,
): UseQueryResult<{ items: PostPromotionListResDisplayType[]; pageInfo: PageInfoType }, Error> => {
  // LP8658: serviceType/unitType/status 정규화는 순수 함수로 분리(단위 테스트 대상). 비-INBOUND/전체 검색은 unitType=null.
  const submitParams = buildPromotionListSubmitParams(params)

  return useQuery({
    queryKey: ['PromotionList', params],
    queryFn: () => postPromotionList(submitParams),
    select: ({ data }): { items: PostPromotionListResDisplayType[]; pageInfo: PageInfoType } => {
      const items = data?.items ?? []
      const statusOrder: Record<string, number> = { ACTIVE: 0, PENDING: 1, ENDED: 2 }

      return {
        items: sortBy(items, (promotion) => statusOrder[promotion.status.code] ?? 999).map(normalizeDiscountScale),
        pageInfo: data?.pageInfo ?? { page: 0, size: 0, totalCount: 0, totalPages: 0 },
      }
    },
    enabled: false,
  })
}

// 프로모션 등록 쿼리
export const usePostPromotion = () => {
  return useMutation({
    mutationFn: (params: PostPromotionReq) => postPromotion(params),
  })
}

export const usePostMFSPartnerList = () => {
  return useQuery({
    queryKey: ['MFSPartnerList'],
    queryFn: postMFSPartnerList,
    select: ({ data }) => data?.partnerIds ?? [],
  })
}

export const usePostBrandList = (params: { partnerIds?: string[] }) => {
  return useQuery({
    queryKey: ['BrandList', params.partnerIds],
    queryFn: () => postBrandList(params),
    // BE 가 brandId 기준 중복 제거한 flat 목록(brands)을 내려준다.
    // 폴백: brands 필드가 없는 구버전 BE 응답이면 그룹 합산으로 대체 (FE 선배포 시 브랜드 목록 공백 방지)
    select: ({ data }) => data?.brands ?? data?.partnerBrands?.flatMap((partnerBrand) => partnerBrand.brands) ?? [],
    // partnerIds 미지정(undefined) = 전 업체 브랜드 조회. 빈 배열은 업체 선택 대기 상태라 조회하지 않음.
    // LOGISPRD-11293 이후 검색 폼은 항상 배열을 넘긴다 — undefined 를 넘기는 곳은 등록 모달뿐이고, 그 상태에선 모달의 브랜드 셀렉트가 잠겨 있어 응답이 쓰이지 않는다
    enabled: params.partnerIds === undefined || params.partnerIds.length > 0,
  })
}

// LOGISPRD-10354 — 프로모션 할인 집계 목록 (파트너·기간 스코프)
export const usePostPromotionSettlementSearch = (
  params: PromotionSettlementSummaryReq,
  enabled: boolean,
): UseQueryResult<{ items: PromotionSettlementSummaryRow[]; pageInfo: PageInfoType }, Error> => {
  return useQuery({
    queryKey: ['PromotionSettlementSummary', params],
    queryFn: () => postPromotionSettlementSearch(params),
    // 소계 고정행의 할인값도 같은 컬럼에서 온다 — 표시 전에 소수부를 뗀다 (LOGISPRD-11422).
    // 기간 프로모션은 할인값이 구간에 있으므로 구간까지 같이 뗀다 (LOGISPRD-11772)
    select: ({ data }) => ({
      items: (data?.items ?? []).map(normalizeDiscountScale),
      pageInfo: data?.pageInfo ?? { page: 0, size: 0, totalCount: 0, totalPages: 0 },
    }),
    enabled,
  })
}

interface ExportExcelPromotionSettlementLinesResult {
  isLoading: boolean
  onExportData: (params: PromotionSettlementLineReq) => void
}

// LOGISPRD-11360 — 프로모션 할인 라인 전체 다운로드 (BE 가 조건대로 전량을 엑셀로 만든다)
export const useExportExcelPromotionSettlementLines = (): ExportExcelPromotionSettlementLinesResult => {
  const excelDownload = useExcelDownload({ config: { method: 'get' } })
  return {
    isLoading: excelDownload.isLoading,
    onExportData: (params: PromotionSettlementLineReq) => {
      excelDownload.onDownloadExcel('/api2/mass/v1/promotions/settlements/lines/excel', {
        partnerId: params.partnerId,
        settlementDateFrom: params.settlementDateFrom,
        settlementDateTo: params.settlementDateTo,
        promotionIds: params.promotionIds,
      })
    },
  }
}

// LOGISPRD-11359 — 프로모션 할인 라인 (프로모션 여러 건을 한 번에)
export const usePostPromotionSettlementDetails = (
  params: PromotionSettlementLineReq,
  enabled: boolean,
): UseQueryResult<PromotionSettlementLineRes | undefined, Error> => {
  return useQuery({
    // 페이지를 넘길 때 직전 페이지를 그대로 두어 "조회결과 없음" 이 스치지 않게 한다
    placeholderData: keepPreviousData,
    queryKey: ['PromotionSettlementLines', params],
    queryFn: () => postPromotionSettlementDetails(params),
    // 할인값은 목록과 같은 컬럼이라 소수부가 붙어 온다 — 표시 전에 뗀다 (LOGISPRD-11422)
    select: ({ data }) =>
      data && {
        ...data,
        promotion: data.promotion && { ...data.promotion, discountValue: stripTrailingZeros(data.promotion.discountValue) },
        lines: data.lines.map((line) => ({ ...line, discountValue: stripTrailingZeros(line.discountValue) })),
      },
    enabled,
  })
}

// LOGISPRD-11315 — 등록 가능한 부과 기준 조합. 등록 모달의 국가·주문유형·서비스 항목 드롭다운 원천
export const useGetPromotionChargeBases = (): UseQueryResult<GetPromotionChargeBaseRes[], Error> =>
  useQuery({
    queryKey: ['PromotionChargeBases'],
    queryFn: getPromotionChargeBases,
    select: ({ data }) => data?.items ?? [],
  })

export const useGetServiceTypeList = () => {
  const { data, ...rest } = useQuery({
    queryKey: ['ServiceTypeList'],
    queryFn: getServiceTypeList,
    select: ({ data }) => data?.items ?? [],
  })

  return {
    data: data || [],
    ...rest,
  }
}

export const useGetPromotionStatusList = () => {
  return useQuery({
    queryKey: ['PromotionStatusList'],
    queryFn: getPromotionStatusList,
    select: ({ data }) => data?.items,
  })
}

export const usePutPromotion = () => {
  return useMutation({
    mutationFn: (params: PutPromotionReq) => putPromotion(params),
  })
}

export const usePatchStopPromotion = () => {
  return useMutation({
    mutationFn: (params: { promotionIds: number[] }) => patchStopPromotion(params),
  })
}

// 일괄등록 업로드 양식 — 확정 양식 파일을 S3 에서 그대로 내려준다 (기타조정금 양식과 같은 방식)
export const usePromotionBulkTemplate = () => {
  const excelDownload = useExcelDownload({
    fileNamePrefix: '프로모션_일괄등록_업로드양식',
    config: { method: 'get' },
  })
  return {
    isLoading: excelDownload.isLoading,
    onDownloadTemplate: () => excelDownload.onDownloadExcel(`/api2/mass/v1/promotions/bulk/template`),
  }
}
